'use client'
import type { FC } from 'react'
import { useCallback } from 'react'
import { DynamicWidgetProps, ProductListWidgetType, StorefrontWidget, StoreType } from 'ecosystem'
import ProductListWidget from 'storefront-modules/products/widgets/ProductListWidget'
import { ProductListVerticalWidget } from 'storefront-modules/products'
import { useSelector } from 'shared-redux'

import { useAppConfigContext } from '#/src/appConfig'
import { useCustomCart } from '#/src/custom/cart/useCustomCart'
import { useSlideOut } from '#/src/custom/controllers'
import { ISlideOutMoreFiltersProps } from '#/src/custom/controllers/slideouts/SlideOutMoreFilters'
import { ISlideOutMoreFilters1 } from '#/src/custom/controllers/slideouts/SlideOutMoreFilters1'
import useProductRequest from '#/src/custom/products/useProductRequest'
import { selectedProductState } from 'shared-redux/state'
import { OverridesContextProvider, OverridesContextType } from 'ui/lib/overrides'

export type CustomProductListWidgetType = 'PRODUCT_LIST' | 'PRODUCT_LIST_1'

interface CustomProductListWidgetProps {
  type: CustomProductListWidgetType
  widget: StorefrontWidget<ProductListWidgetType>
  overrides?: OverridesContextType
  dynamicProps?: DynamicWidgetProps
}

const CustomProductListWidget: FC<CustomProductListWidgetProps> = ({
  dynamicProps,
  overrides,
  widget,
  type
}) => {
  const appConfig = useAppConfigContext()
  const productsState = useSelector(selectedProductState)
  const sortingOptions = appConfig.sortingOptions
  const sliderMaxPrice = appConfig.sliderMaxPrice
  const sliderDefaultValues = appConfig.sliderDefaultValues
  const { handleAddToCart, cartItems } = useCustomCart()
  const { closeSlideOut, openSlideOut } = useSlideOut()
  const { handleProductRequest } = useProductRequest()
  const apiPath = '/api/products/get-initial-products'

  const openMoreFilters = useCallback(() => {
    openSlideOut<ISlideOutMoreFiltersProps | ISlideOutMoreFilters1>({
      title: 'Filter',
      componentId: type === 'PRODUCT_LIST' ? 'moreFilters' : 'moreFilters1',
      drawerProps: {
        placement: 'right',
        size: 'md'
      }
    })
  }, [openSlideOut, type])

  return (
    <OverridesContextProvider overrides={{ ...overrides }}>
      {(() => {
        switch (type) {
          case 'PRODUCT_LIST': {
            return (
              <ProductListWidget
                showsPrice={appConfig.storeType !== StoreType.MARKETPLACE}
                filterProps={{
                  sortingOptions,
                  sliderMaxPrice,
                  sliderDefaultValues,
                  filtersBeforeShowMore: appConfig.filtersBeforeShowMore
                }}
                widget={widget as StorefrontWidget<ProductListWidgetType>}
                widgetId={widget.widget.name}
                cartItems={cartItems}
                {...{
                  dynamicProps,
                  productsState,
                  onAddToCart: handleAddToCart,
                  onMore: openMoreFilters,
                  onProductRequest: handleProductRequest,
                  paths: {
                    getInitialProducts: apiPath,
                    productsUrlPath: appConfig.paths.productsUrlPath
                  }
                }}
              />
            )
          }

          case 'PRODUCT_LIST_1': {
            return (
              <ProductListVerticalWidget
                widget={widget as StorefrontWidget<ProductListWidgetType>}
                showsPrice={appConfig.storeType !== StoreType.MARKETPLACE}
                widgetId={(widget as StorefrontWidget<ProductListWidgetType>).widget.name}
                cartItems={cartItems}
                {...{
                  dynamicProps,
                  productsState,
                  sortingOptions,
                  onAddToCart: handleAddToCart,
                  onMore: openMoreFilters,
                  onSearchUpdate: closeSlideOut,
                  sliderMaxPrice,
                  sliderDefaultValues,
                  onProductRequest: handleProductRequest,
                  paths: {
                    getInitialProducts: apiPath,
                    productsUrlPath: appConfig.paths.productsUrlPath
                  }
                }}
              />
            )
          }

          default:
            return null
        }
      })()}
    </OverridesContextProvider>
  )
}

export default CustomProductListWidget
